import {
  type QuestionGradingResult,
  type QuestionGraderInput,
  type QuestionGraderResultInput,
  type ExamGraderServiceInput,
  type ExamGraderResult,
  type QConfigStructurePartResult,
} from '../types';

function buildExamGradingResult(input: QuestionGraderInput): QuestionGradingResult {
  const { examPaperItem, question } = input;
  const answerValue = examPaperItem.answer.value;
  const correctValue = question.metadata.correctAnswer.value;
  const isCorrect = answerValue === correctValue;

  return {
    id: examPaperItem.id,
    questionId: examPaperItem.questionId,
    score: isCorrect ? question.metadata.score : 0,
    isCorrect,
    fullScore: question.metadata.score,
    correctAnswer: correctValue,
    answer: answerValue,
    questionMetadata: question.metadata,
    questionTags: question.metadata.questionTags ?? [],
    order: question.order,
  };
}

function grade(examGraderResultInput: QuestionGraderResultInput) {
  const { examPaperItems, questions } = examGraderResultInput;

  if (examPaperItems.length === 0) {
    return [];
  }
  if (questions.length === 0) {
    return [];
  }

  const results: QuestionGradingResult[] = examPaperItems.map((examPaperItem) => {
    const question = questions.find((q) => q.id === examPaperItem.questionId);
    if (!question) throw new Error(`Question not found for id=${examPaperItem.questionId}`);
    return buildExamGradingResult({ examPaperItem, question });
  });

  return results;
}

function calculatePassingCriteria(percent: number, fullScore: number, totalScore: number) {
  return totalScore >= (fullScore * percent) / 100;
}

export function calculateExamGradingResults(
  examGraderServiceInput: ExamGraderServiceInput,
): ExamGraderResult {
  const { examGraderResultInput, examEvent } = examGraderServiceInput;
  const questionGradingResults = grade(examGraderResultInput);

  const passingCriteria = examEvent.exam?.metadata.passingCriteria;

  let fullScore = 0;
  let totalScore = 0;
  let correctAnswerCount = 0;
  let incorrectAnswerCount = 0;

  const structureScores: Record<string, QConfigStructurePartResult> = {};

  for (const result of questionGradingResults) {
    totalScore += result.score;
    fullScore += result.fullScore;
    if (result.isCorrect) {
      correctAnswerCount += 1;
    } else {
      incorrectAnswerCount += 1;
    }

    for (const part of passingCriteria.byStructure) {
      const { value, qConfigStructureId } = part;

      if (structureScores[qConfigStructureId] === undefined) {
        structureScores[qConfigStructureId] = {
          percentPassing: value,
          fullScore: 0,
          totalScore: 0,
          name: '',
        };
      }

      const matchQuestionTag = result.questionTags.find(
        (tag) => tag.category === 'PART' && tag.refId === qConfigStructureId,
      );

      if (matchQuestionTag) {
        structureScores[qConfigStructureId].totalScore += result.score;
        structureScores[qConfigStructureId].fullScore += result.fullScore;
        structureScores[qConfigStructureId].name = matchQuestionTag.name;
      }
    }
  }

  const isTotalPass = calculatePassingCriteria(passingCriteria.total, fullScore, totalScore);
  const isByStructurePass = Object.values(structureScores).every(
    ({ percentPassing, fullScore, totalScore }) =>
      calculatePassingCriteria(percentPassing, fullScore, totalScore),
  );

  return {
    isPass: isTotalPass && isByStructurePass,
    correctAnswerCount,
    incorrectAnswerCount,
    totalQuestion: questionGradingResults.length,
    questionGradingResults,
    fullScore,
    totalScore,
    structureScores,
  };
}
